import { ProductPurchaseModel } from 'api-hooks/product/order';
import { Input } from 'components/elements';
import { useFormState } from 'components/elements/form';
import Text from 'components/elements/text';
import AgentSelectInput from 'legacy-modules/master/agent/components/select-input';
import PaymentChannelSelectInput from 'legacy-modules/master/payment-channel/components/select-input';
import AgentSupendToast from 'legacy-modules/order/component/agent-suspend-toast';
import TransactionAt from 'legacy-modules/sales/component/transaction-at';
import {
  CardSection,
  FullContainer,
  HalfContainer,
  SectionContainer,
  SeparatorVertical,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { ProductPurchaseFormType } from './form-type';

interface Props {
  productPurchase?: ProductPurchaseModel;
}

export default function OrderDetailField(props: Props) {
  const { t } = useTranslation();
  const { setValue, getValues } = useFormContext<ProductPurchaseFormType>();
  const [agentId, total, remainingTransactionQuota] =
    useWatch<ProductPurchaseFormType>({
      name: ['agentId', 'total', 'remainingTransactionQuota'],
    });

  const isEdit = !!getValues('productPurchase');
  const { editable } = useFormState();

  const isExceedingQuota =
    remainingTransactionQuota && total > remainingTransactionQuota;

  React.useEffect(() => {
    setValue('isExceedingQuota', isExceedingQuota || false);
  }, [setValue, isExceedingQuota]);

  React.useEffect(() => {
    if (!agentId || agentId === '') {
      setValue('agentStatus', '');
      setValue('remainingTransactionQuota', null);
      setValue('isExceedingQuota', false);
      setValue('pic', {
        description: '',
        email: '',
        name: '',
        phone: '',
      });
    }
  }, [agentId, setValue]);

  const onAfterAgentDetailChange = React.useCallback(
    (result) => {
      if (editable) {
        const remainingTransactionQuota =
          result.system.remainingTransactionQuota;
        const isExceedingQuota =
          result.system.isTransactionLimited &&
          total > remainingTransactionQuota;

        setValue('isExceedingQuota', isExceedingQuota);
        setValue(
          'remainingTransactionQuota',
          remainingTransactionQuota || null,
        );

        setValue('agentStatus', result.status);
        setValue('pic', {
          description: result.description || '',
          email: result.email || '',
          name: result.name || '',
          phone: result.phone || '',
        });
      }
    },
    [editable, setValue, total],
  );

  return (
    <CardSection style={{ paddingBottom: 0 }}>
      <FullContainer>
        <Text variant="HeadingXSmall">{t('common:purchase_details')}</Text>
        <SeparatorVertical $margin={24} />
        <SectionContainer>
          {isEdit && (
            <HalfContainer>
              <Input
                type="text"
                name="orderNumber"
                label={t('common:order_number')}
                placeholder={t('common:order_number')}
                disabled
              />
            </HalfContainer>
          )}
          <HalfContainer
            $style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
            }}
          >
            <TransactionAt
              name="transactionAt"
              clearable={false}
              label={t('common:transaction_at')}
              placeholder={t('common:transaction_at')}
              hasTime
              required
            />
          </HalfContainer>
        </SectionContainer>
        <SectionContainer>
          <HalfContainer>
            <AgentSelectInput
              name="agentId"
              required
              remainingTransactionQuota={remainingTransactionQuota}
              onAfterDetailChange={onAfterAgentDetailChange}
            />
          </HalfContainer>
          <HalfContainer>
            <PaymentChannelSelectInput name="paymentChannelId" />
          </HalfContainer>
        </SectionContainer>
      </FullContainer>
      <AgentSupendToast />
    </CardSection>
  );
}
